import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import {
  ArrowDownward,
  ArrowUpward,
  ChevronRight,
  ExpandMore,
  FolderOutlined,
  MessageOutlined,
  MoreVert
} from '@material-ui/icons';
import api from 'services/api';
import PropTypes from 'prop-types';
import { ANAO, ASIM } from 'consts';
import React, { useEffect, useState } from 'react';
import MsgWhatsAppPassageiroList from './Mensagens/MsgWhatsAppPassageiroList';
import GrupoMsgWhatsAppPassageiroList from './Grupos/GrupoMsgWhatsAppPassageiroList';

const ESTILO_LISTA = {
  height: 310,
  overflowY: 'auto',
  border: '1px solid #ddd'
};

const ESTILO_ICONE = {
  marginRight: 8
};

const TITULOS_EDITOR = {
  grupo: 'Cadastro de grupo',
  mensagem: 'Cadastro de mensagem',
  vincular: 'Vincular mensagem',
  desvincular: 'Remover vínculo'
};

export default function WhatsAppEstrutura({ open, onClose, onChanged }) {
  const [grupos, setGrupos] = useState([]);
  const [mensagens, setMensagens] = useState([]);
  const [composicoes, setComposicoes] = useState({});
  const [expandidos, setExpandidos] = useState({});
  const [selecionado, setSelecionado] = useState(null);
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [menu, setMenu] = useState(null);
  const [editor, setEditor] = useState('');
  const [consulta, setConsulta] = useState('');
  const [codigo, setCodigo] = useState(null);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [texto, setTexto] = useState('');
  const [inativo, setInativo] = useState(false);
  const [vinculo, setVinculo] = useState('');
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  /* Retorna a mensagem pelo código */
  const getMensagem = (idmsgwhats) => mensagens.find(
    m => Number(m.idmsgwhats) === Number(idmsgwhats)
  );

  const grupoAtual = selecionado && grupos.find(
    g => Number(g.idgrupomsgwhats) === Number(selecionado.grupoId)
  );

  const mensagemAtual = selecionado && getMensagem(selecionado.mensagemId);

  const gruposVisiveis = grupos.filter(g => mostrarInativos || g.inativo !== ASIM);

  const disableSalvar = loading ||
    (editor === 'grupo' && !nome.trim()) ||
    (editor === 'mensagem' && (!descricao.trim() || !texto.trim())) ||
    (editor === 'vincular' && !vinculo);

  /* Atualiza os grupos, as mensagens e as composições */
  const refresh = async () => {
    const [g, m] = await Promise.all([
      api.get('/crm/whatsapp/grupos'),
      api.get('/crm/whatsapp/mensagens')
    ]);

    setGrupos(g.data || []);
    setMensagens(m.data || []);

    const resultados = await Promise.all((g.data || []).map(
      x => api.get(`/crm/whatsapp/grupos/${x.idgrupomsgwhats}/composicao`).then(
        r => [x.idgrupomsgwhats, r.data.mensagens || []]
      )
    ));

    setComposicoes(Object.fromEntries(resultados));
  };

  /* Carrega os dados iniciais da tela */
  useEffect(() => {
    if (open) {
      refresh().catch(
        e => setErro(e.response?.data?.error || 'Erro ao carregar a estrutura!')
      );
    }
  }, [open]);

  /* Fecha a consulta e atualiza a árvore */
  const fecharConsulta = async () => {
    setConsulta('');
    setEditor('');

    try {
      await refresh();

      if (onChanged) onChanged();
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao atualizar a árvore!');
    }
  };

  /* Abre a consulta de grupos ou de mensagens */
  const abrirConsulta = (tipo) => () => {
    setEditor('');
    setConsulta(tipo);
  };

  /* Executa a operação, atualiza a árvore e fecha o editor */
  const run = async (fn) => {
    setLoading(true);
    setErro('');

    try {
      await fn();
      await refresh();

      if (onChanged) onChanged();

      setEditor('');
    } catch (e) {
      setErro(e.response?.data?.error || 'Não foi possível concluir a operação!');
    } finally {
      setLoading(false);
    }
  };

  /* Abre o editor de grupo */
  const editarGrupo = (grupo) => {
    setCodigo(grupo?.idgrupomsgwhats || null);
    setNome(grupo?.nome || '');
    setInativo(grupo?.inativo === ASIM);
    setEditor('grupo');
  };

  /* Abre o editor de mensagem */
  const editarMensagem = (mensagem) => {
    setCodigo(mensagem?.idmsgwhats || null);
    setDescricao(mensagem?.descricao || '');
    setTexto(mensagem?.msg || '');
    setInativo(mensagem?.inativo === ASIM);
    setEditor('mensagem');
  };

  /* Executa a ação escolhida no menu */
  const abrirAcao = (acao) => () => {
    setMenu(null);

    if (acao === 'novoGrupo') editarGrupo(null);
    if (acao === 'editarGrupo' && grupoAtual) editarGrupo(grupoAtual);
    if (acao === 'novaMensagem') editarMensagem(null);
    if (acao === 'editarMensagem' && mensagemAtual) editarMensagem(mensagemAtual);

    if (acao === 'vincular') {
      setVinculo('');
      setEditor('vincular');
    }

    if (acao === 'desvincular' && selecionado?.mensagemId) setEditor('desvincular');
  };

  /* Clicar para salvar */
  const salvar = () => {
    const metodo = codigo ? 'put' : 'post';
    const sufixo = codigo ? `/${codigo}` : '';

    if (editor === 'grupo') {
      return run(() => api[metodo](
        `/crm/whatsapp/grupos${sufixo}`,
        {
          nome,
          inativo: inativo ? ASIM : ANAO
        }
      ));
    }

    if (editor === 'mensagem') {
      return run(() => api[metodo](
        `/crm/whatsapp/mensagens${sufixo}`,
        {
          descricao,
          msg: texto,
          inativo: inativo ? ASIM : ANAO
        }
      ));
    }

    if (editor === 'vincular') {
      return run(() => api.post(
        `/crm/whatsapp/grupos/${grupoAtual.idgrupomsgwhats}/mensagens`,
        { idmsgwhats: vinculo }
      ));
    }

    if (editor === 'desvincular') {
      return run(() => api.delete(
        `/crm/whatsapp/grupos/${selecionado.grupoId}/mensagens/${selecionado.mensagemId}`
      ));
    }
  };

  /* Move o grupo para cima ou para baixo */
  const moverGrupo = (direcao) => () => {
    if (!grupoAtual) return;

    return run(() => api.put(
      `/crm/whatsapp/grupos/${grupoAtual.idgrupomsgwhats}/ordem`,
      { direcao }
    ));
  };

  /* Seleciona o grupo e expande ou recolhe seus itens */
  const onClickGrupo = (gid) => () => {
    setSelecionado({ grupoId: gid });

    setExpandidos(v => ({
      ...v,
      [gid]: !v[gid]
    }));
  };

  /* Seleciona a mensagem do grupo */
  const onClickMensagem = (selecao) => () => {
    setSelecionado(selecao);
  };

  /* Seleciona o nó e abre o menu de ações com o botão direito */
  const onContextMenuNo = (selecao) => (event) => {
    event.preventDefault();

    setSelecionado(selecao);
    setMenu(event.currentTarget);
  };

  /* Evento onChange dos componentes */
  const onChangeMostrarInativos = (event) => setMostrarInativos(event.target.checked);
  const onChangeNome = (event) => setNome(event.target.value);
  const onChangeDescricao = (event) => setDescricao(event.target.value);
  const onChangeTexto = (event) => setTexto(event.target.value);
  const onChangeInativo = (event) => setInativo(event.target.checked);
  const onChangeVinculo = (event) => setVinculo(event.target.value);

  /* Abre e fecha o menu de ações */
  const onClickAcoes = (event) => setMenu(event.currentTarget);
  const onCloseMenu = () => setMenu(null);

  /* Fecha o editor */
  const onClickCancelar = () => setEditor('');

  /* Renderiza as mensagens do grupo */
  const renderMensagens = (gid) => (composicoes[gid] || []).filter(
    m => mostrarInativos || getMensagem(m.idmsgwhats)?.inativo !== ASIM
  ).map((m, index) => {
    const selecao = {
      grupoId: gid,
      mensagemId: m.idmsgwhats
    };

    return (
      <ListItem
        button
        key={`${gid}-${m.idmsgwhats}-${index}`}
        style={{ paddingLeft: 65 }}
        selected={selecionado?.grupoId === gid && selecionado?.mensagemId === m.idmsgwhats}
        onClick={onClickMensagem(selecao)}
        onContextMenu={onContextMenuNo(selecao)}
      >
        <MessageOutlined
          fontSize="small"
          style={ESTILO_ICONE}
        />

        <ListItemText
          primary={`${getMensagem(m.idmsgwhats)?.descricao || 'Mensagem'} [${m.idmsgwhats}]`}
        />
      </ListItem>
    );
  });

  /* Renderiza o grupo e suas mensagens */
  const renderGrupo = (g) => {
    const gid = g.idgrupomsgwhats;
    const aberto = !!expandidos[gid];

    return (
      <React.Fragment key={gid}>
        <ListItem
          button
          selected={selecionado?.grupoId === gid && !selecionado?.mensagemId}
          onClick={onClickGrupo(gid)}
          onContextMenu={onContextMenuNo({ grupoId: gid })}
        >
          {aberto ? (
            <ExpandMore fontSize="small" />
          ) : (
            <ChevronRight fontSize="small" />
          )}

          <FolderOutlined
            fontSize="small"
            style={ESTILO_ICONE}
          />

          <ListItemText
            primary={`${g.nome} [${gid}]${g.inativo === ASIM ? ' (Inativo)' : ''}`}
          />
        </ListItem>

        {aberto && renderMensagens(gid)}
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Estrutura de mensagens WhatsApp
        </DialogTitle>

        <DialogContent dividers>
          {erro && (
            <Typography color="error">
              {erro}
            </Typography>
          )}

          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <IconButton
              size="small"
              title="Subir grupo"
              disabled={!grupoAtual || loading}
              onClick={moverGrupo('up')}
            >
              <ArrowUpward />
            </IconButton>

            <IconButton
              size="small"
              title="Descer grupo"
              disabled={!grupoAtual || loading}
              onClick={moverGrupo('down')}
            >
              <ArrowDownward />
            </IconButton>

            <IconButton
              size="small"
              title="Ações"
              onClick={onClickAcoes}
            >
              <MoreVert />
            </IconButton>
          </Box>

          <Menu
            anchorEl={menu}
            open={Boolean(menu)}
            onClose={onCloseMenu}
          >
            <MenuItem onClick={abrirAcao('novoGrupo')}>
              Inserir grupo
            </MenuItem>

            <MenuItem
              disabled={!grupoAtual}
              onClick={abrirAcao('editarGrupo')}
            >
              Alterar grupo
            </MenuItem>

            <MenuItem onClick={abrirAcao('novaMensagem')}>
              Inserir mensagem
            </MenuItem>

            <MenuItem
              disabled={!mensagemAtual}
              onClick={abrirAcao('editarMensagem')}
            >
              Alterar mensagem
            </MenuItem>

            <MenuItem
              disabled={!grupoAtual}
              onClick={abrirAcao('vincular')}
            >
              Vincular mensagem ao grupo
            </MenuItem>

            <MenuItem
              disabled={!selecionado?.mensagemId}
              onClick={abrirAcao('desvincular')}
            >
              Remover vínculo
            </MenuItem>
          </Menu>

          <List
            dense
            style={ESTILO_LISTA}
          >
            {gruposVisiveis.map(renderGrupo)}
          </List>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mt={1}
            flexWrap="wrap"
          >
            <FormControlLabel
              label="Mostrar inativos"
              control={
                <Checkbox
                  checked={mostrarInativos}
                  onChange={onChangeMostrarInativos}
                  color="primary"
                />
              }
            />

            <Box>
              <Button onClick={abrirConsulta('grupo')}>
                Grupos
              </Button>

              <Button onClick={abrirConsulta('mensagem')}>
                Mensagens
              </Button>
            </Box>
          </Box>

          {(!consulta && editor) && (
            <Box
              mt={2}
              p={2}
              border="1px solid #ddd"
              borderRadius={4}
            >
              <Typography variant="subtitle1">
                {TITULOS_EDITOR[editor]}
              </Typography>

              {editor === 'grupo' && (
                <TextField
                  fullWidth
                  margin="dense"
                  label="Nome do grupo"
                  value={nome}
                  onChange={onChangeNome}
                />
              )}

              {editor === 'mensagem' && (
                <>
                  <TextField
                    fullWidth
                    margin="dense"
                    label="Descrição"
                    value={descricao}
                    onChange={onChangeDescricao}
                  />

                  <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    margin="dense"
                    label="Texto da mensagem"
                    value={texto}
                    onChange={onChangeTexto}
                  />
                </>
              )}

              {(editor === 'grupo' || editor === 'mensagem') && (
                <FormControlLabel
                  label="Inativo"
                  control={
                    <Checkbox
                      checked={inativo}
                      onChange={onChangeInativo}
                    />
                  }
                />
              )}

              {editor === 'vincular' && (
                <TextField
                  select
                  fullWidth
                  label={`Mensagem para ${grupoAtual?.nome || 'grupo'}`}
                  value={vinculo}
                  onChange={onChangeVinculo}
                  margin="dense"
                >
                  {mensagens.filter(m => mostrarInativos || m.inativo !== ASIM).map(m => (
                    <MenuItem
                      key={m.idmsgwhats}
                      value={m.idmsgwhats}
                    >
                      {m.descricao} [{m.idmsgwhats}]
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {editor === 'desvincular' && (
                <Typography>
                  Remover esta mensagem do grupo? A mensagem continuará cadastrada e poderá estar vinculada a outros grupos.
                </Typography>
              )}

              <Box mt={2}>
                <Button
                  variant="contained"
                  color={editor === 'desvincular' ? 'secondary' : 'primary'}
                  disabled={disableSalvar}
                  onClick={salvar}
                >
                  {loading ? (
                    <CircularProgress size={18} />
                  ) : (
                    editor === 'desvincular' ? 'Confirmar remoção' : 'Salvar'
                  )}
                </Button>

                <Button onClick={onClickCancelar}>
                  Cancelar
                </Button>
              </Box>
            </Box>
          )}

          <Typography
            variant="caption"
            color="textSecondary"
          >
            Clique em um grupo para expandir. Use o botão de ações ou clique com o botão direito sobre um nó. A remoção de vínculo não exclui a mensagem.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {consulta === 'grupo' && (
        <GrupoMsgWhatsAppPassageiroList
          modal
          open
          onClose={fecharConsulta}
          onChanged={refresh}
        />
      )}

      {consulta === 'mensagem' && (
        <MsgWhatsAppPassageiroList
          modal
          open
          onClose={fecharConsulta}
          onChanged={refresh}
        />
      )}
    </React.Fragment>
  );
};

WhatsAppEstrutura.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onChanged: PropTypes.func
};